import React, { Component, FormEvent } from 'react';
import FormGroup from '../common/FormGroup';
import { CardElement, ElementsConsumer } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import * as Sentry from '@sentry/browser';
import './BillingForm.css';
import Input from '../common/Input';
import Button from '../common/Button';
import { FormattedMessage } from 'react-intl';
import { createFragmentContainer, RelayProp } from 'react-relay';
import { graphql } from 'babel-plugin-relay/macro';
import { BillingForm_subscriptionPlan } from './__generated__/BillingForm_subscriptionPlan.graphql';
import PurchaseUpgradeSubscriptionMutation from '../../mutations/PurchaseUpgradeSubscriptionMutation';

interface BillingFormBaseProps {
  subscriptionPlan: BillingForm_subscriptionPlan;
  relay: RelayProp;
}

interface BillingFormInnerProps extends BillingFormBaseProps {
  stripe: Stripe | null;
  elements: StripeElements | null;
}

interface BillingFormState {
  name: string;
  error: string | null;
  isSubmitting: boolean;
}

class BillingFormInner extends Component<BillingFormInnerProps, BillingFormState> {
  state: BillingFormState = {
    name: '',
    error: null,
    isSubmitting: false,
  };

  handleSubmit = async (evt: FormEvent) => {
    evt.preventDefault();
    if (!this.state.name) {
      return this.setState({ error: 'Name is empty' });
    }
    const { stripe, elements, relay, subscriptionPlan } = this.props;
    if (!stripe || !elements || this.state.isSubmitting) return;
    this.setState({ error: null, isSubmitting: true });
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        this.setState({ error: 'Card element not found' });
        return;
      }
      const { token, error } = await stripe.createToken(cardElement, {
        name: this.state.name,
      });
      if (error) {
        this.setState({ error: error.message || null });
      } else if (!token) {
        this.setState({ error: 'Payment failed. Please try again' });
      } else {
        // optimistically track here
        window.fbq('track', 'Purchase', {
          currency: 'USD',
          value: subscriptionPlan.price,
        });
        await PurchaseUpgradeSubscriptionMutation.commit(
          relay.environment,
          subscriptionPlan.id,
          token.id,
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ error: error.message });
    }
    this.setState({ isSubmitting: false });
  };

  render() {
    return (
      <form onSubmit={this.handleSubmit}>
        <FormGroup label="Name">
          <Input
            id="billingName"
            required={true}
            value={this.state.name}
            onChange={evt => this.setState({ name: evt.target.value })}
          />
        </FormGroup>
        <FormGroup label="Card">
          <CardElement
            id="cardNumberInput"
            className="BillingForm-input"
            options={{ style: { base: { lineHeight: '24px' } } }}
          />
        </FormGroup>
        {this.state.error && <div className="pa2 red">{this.state.error}</div>}
        <div className="mt5">
          <FormGroup>
            <Button
              id="checkoutButton"
              fullWidth
              type="submit"
              loading={this.state.isSubmitting}
              disabled={this.state.isSubmitting}
            >
              <FormattedMessage
                id="BillingForm.checkout"
                defaultMessage="Checkout"
              />
            </Button>
          </FormGroup>
        </div>
      </form>
    );
  }
}

const BillingFormWithStripe = (props: BillingFormBaseProps) => (
  <ElementsConsumer>
    {({ stripe, elements }) => (
      <BillingFormInner {...props} stripe={stripe} elements={elements} />
    )}
  </ElementsConsumer>
);

export default createFragmentContainer(
  BillingFormWithStripe,
  {
    subscriptionPlan: graphql`
      fragment BillingForm_subscriptionPlan on SubscriptionPlan {
        id
        price
      }
    `,
  },
);
